// src/components/utils/modals/CreateOrderModal.tsx
"use client"

import React, { useState, useEffect, useRef } from "react"
import Input from "../../ui/Input"
import Button from "../../ui/Button"
import { FaSave, FaTimes } from "react-icons/fa"

interface CreateOrderModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (name: string) => void
  defaultName: string
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ isOpen, onClose, onSave, defaultName }) => {
  const [name, setName] = useState(defaultName)
  const [error, setError] = useState("")
  const modalRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setName(defaultName)
      setError("")
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen, defaultName])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }
    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [isOpen, onClose])

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  const handleSaveClick = () => {
    if (name.trim() === "") {
      setError("El nombre no puede estar vacío.")
      return
    }
    onSave(name.trim())
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveClick();
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm" onClick={handleClickOutside}>
      <div ref={modalRef} className="bg-white w-full max-w-md max-h-[90vh] rounded-xl shadow-lg flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[#2d53da] rounded-t-xl">
          <h3 className="text-lg font-bold text-white">Crear Nueva Ventana</h3>
          <button onClick={onClose} className="text-white hover:text-white/80 transition-colors">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-white">
          <Input
            ref={inputRef}
            label="Nombre de la ventana"
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (error) setError("")
            }}
            onKeyDown={handleKeyDown}
            required
            error={error}
            fullWidth
          />
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <Button variant="outline" onClick={onClose} icon={<FaTimes />}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClick} variant="primary" icon={<FaSave />}>
            Crear Ventana
          </Button>
        </div>
      </div>
    </div>
  )
}

export default CreateOrderModal